/**
 * Page de création et d'édition d'un To Do,
 * avec possibilité de créer un contact à la volée.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getTodo, createTodo, updateTodo } from "../api/todoApi";
import { getContacts, createContact } from "../api/contactApi";
import { getTeamContacts } from "../api/teamApi";
import { getTags, createTag } from "../api/tagApi";
import { getUsers } from "../api/userApi";
import { useAuth } from "../contexts/AuthContext";
import { useTeamView } from "../contexts/TeamViewContext";
import TagSelector from "../components/TagSelector";
import "./PageStyles.css";

export default function TodoFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { teamView } = useTeamView();
  const fromContactId = searchParams.get("contact_id");
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showNewContact, setShowNewContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    last_name: "", first_name: "", company: "", job_title: "",
    email: "", phone: "", comments: "", visibility: "private",
  });
  const [form, setForm] = useState({
    contact_id: fromContactId || "",
    responsable_user_id: user?.id ? String(user.id) : "",
    description: "",
    due_date: "",
    stake_level: "",
    urgency_level: "",
    status: "in_progress",
    tag_ids: [],
  });

  useEffect(() => {
    loadData();
  }, [id, teamView]);

  async function loadData() {
    // Le menu déroulant des contacts suit le mode d'affichage : en vue équipe
    // il inclut les contacts encadrés, en vue perso uniquement les contacts
    // perso (les siens + publics).
    const [contactsData, tagsData, usersData] = await Promise.all([
      teamView ? getTeamContacts() : getContacts(),
      getTags(),
      getUsers(),
    ]);
    setContacts(contactsData);
    setUsers(usersData);
    setAllTags(tagsData);

    if (isEdit) {
      const todo = await getTodo(id);
      setForm({
        contact_id: todo.contact_id,
        responsable_user_id: todo.responsable_user_id ? String(todo.responsable_user_id) : "",
        description: todo.description || "",
        due_date: todo.due_date || "",
        stake_level: todo.stake_level || "",
        urgency_level: todo.urgency_level || "",
        status: todo.status,
        tag_ids: todo.tags.map((t) => t.id),
      });
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleCreateTag(label) {
    const tag = await createTag(label);
    setAllTags([...allTags, tag]);
    setForm({ ...form, tag_ids: [...form.tag_ids, tag.id] });
  }

  async function handleCreateContact() {
    setError("");
    if (!contactForm.last_name.trim()) {
      setError("Le nom du contact est obligatoire.");
      return;
    }
    try {
      const newContact = await createContact(contactForm);
      setContacts([...contacts, newContact]);
      setForm({ ...form, contact_id: String(newContact.id) });
      setShowNewContact(false);
      setContactForm({
        last_name: "", first_name: "", company: "", job_title: "",
        email: "", phone: "", comments: "", visibility: "private",
      });
    } catch {
      setError("Erreur lors de la création du contact.");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.contact_id) {
      setError("Veuillez sélectionner un contact.");
      return;
    }
    try {
      const payload = {
        ...form,
        contact_id: Number(form.contact_id),
        responsable_user_id: form.responsable_user_id ? Number(form.responsable_user_id) : null,
        stake_level: form.stake_level || null,
        urgency_level: form.urgency_level || null,
        due_date: form.due_date || null,
      };
      if (isEdit) {
        await updateTodo(id, payload);
      } else {
        await createTodo(payload);
      }
      if (fromContactId) {
        navigate(`/contacts/${fromContactId}`);
      } else {
        navigate("/todos");
      }
    } catch {
      setError("Erreur lors de l'enregistrement.");
    }
  }

  return (
    <div className="page">
      <h2>{isEdit ? "Modifier le To Do" : "Nouveau To Do"}</h2>
      <div className="form-card" style={{ marginTop: "1rem" }}>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Contact</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <select
                name="contact_id"
                value={form.contact_id}
                onChange={handleChange}
                disabled={isEdit}
                style={{ flex: 1 }}
              >
                <option value="">-- Sélectionner --</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.last_name} {c.first_name} — {c.company || ""}
                  </option>
                ))}
              </select>
              {!isEdit && (
                <button type="button" className="btn btn-small" onClick={() => setShowNewContact(!showNewContact)}>
                  {showNewContact ? "Annuler" : "Nouveau contact"}
                </button>
              )}
            </div>
          </div>

          {showNewContact && (
            <div className="inline-contact-section">
              <h4>Créer un nouveau contact</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input value={contactForm.last_name} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Prénom</label>
                  <input value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Société</label>
                  <input value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Fonction</label>
                  <input value={contactForm.job_title} onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input type="tel" inputMode="tel" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
                </div>
              </div>
              <button type="button" className="btn btn-primary btn-small" onClick={handleCreateContact}>Créer et sélectionner</button>
            </div>
          )}

          <div className="form-group">
            <label>Responsable</label>
            <select
              name="responsable_user_id"
              value={form.responsable_user_id}
              onChange={handleChange}
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.last_name} {u.first_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Date d'échéance</label>
              <input type="date" name="due_date" value={form.due_date} onChange={handleChange} />
            </div>
            <div className="form-group" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Enjeu</label>
              <select name="stake_level" value={form.stake_level} onChange={handleChange}>
                <option value="">—</option>
                <option value="A">A — Fort</option>
                <option value="B">B — Moyen</option>
                <option value="C">C — Faible</option>
              </select>
            </div>
            <div className="form-group">
              <label>Urgence</label>
              <select name="urgency_level" value={form.urgency_level} onChange={handleChange}>
                <option value="">—</option>
                <option value="1">1 — Urgent</option>
                <option value="2">2 — Normal</option>
                <option value="3">3 — Peu urgent</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Tags</label>
            <TagSelector
              allTags={allTags}
              selectedIds={form.tag_ids}
              onChange={(ids) => setForm({ ...form, tag_ids: ids })}
              onCreateTag={handleCreateTag}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {isEdit ? "Enregistrer" : "Créer"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate("/todos")}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
